import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ListChecks, ShieldCheck, Inbox, FileText, ArrowRight, FilePlus2, Compass, LayoutGrid } from "lucide-react";
import api from "@/lib/api";
import { usePerms } from "@/lib/perms";
import { CATEGORY_META } from "@/i18n/translations";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const { isAdmin, can } = usePerms();

  useEffect(() => {
    if (isAdmin) api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div data-testid="editor-dashboard">
        <h1 className="mb-1 font-serif text-3xl text-anthracite">Espace éditeur</h1>
        <p className="mb-8 text-sm text-anthracite/50">Vous pouvez saisir des brouillons sur les modules ci-dessous. La publication, la qualification et la suppression restent réservées à l'admin principal.</p>
        <div className="mb-8 flex flex-wrap gap-2">
          {[["create", "Saisie"], ["update", "Édition"], ["delete", "Suppression"]].map(([k, l]) => (
            <span key={k} data-testid={`editor-perm-badge-${k}`} className={`rounded-full border px-3 py-1 text-xs ${can(k) ? "border-anthracite bg-anthracite text-ivory" : "border-tertiary text-anthracite/40"}`}>{l}{can(k) ? "" : " — non accordée"}</span>
          ))}
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          {[["Opportunités", "/admin/opportunities", Compass], ["Projets", "/admin/projects", LayoutGrid], ["Demandes", "/admin/requests", Inbox]].map(([label, to, Icon]) => (
            <Link key={to} to={to} className="group border border-tertiary bg-white p-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(0,0,0,0.05)]">
              <Icon className="mb-4 h-5 w-5 text-champagne" strokeWidth={1.5} />
              <p className="font-serif text-xl text-anthracite">{label}</p>
            </Link>
          ))}
        </div>
      </div>
    );
  }

  const cards = [
    { label: "Fiches publiées", value: stats?.listings_published, sub: `${stats?.listings_total ?? 0} au total`, icon: ListChecks, to: "/admin/listings" },
    { label: "Soumissions en attente", value: stats?.submissions_pending, sub: `${stats?.submissions_total ?? 0} au total`, icon: FilePlus2, to: "/admin/submissions" },
    { label: "Vérifiées RSTI", value: stats?.verified, sub: "verified / premium", icon: ShieldCheck, to: "/admin/listings" },
    { label: "Nouvelles demandes", value: stats?.requests_new, sub: `${stats?.requests_total ?? 0} au total`, icon: Inbox, to: "/admin/requests" },
  ];

  return (
    <div>
      <h1 className="mb-1 font-serif text-3xl text-anthracite">Tableau de bord</h1>
      <p className="mb-8 text-sm text-anthracite/50">Gérez la plateforme RSTI sans toucher au code.</p>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => (
          <Link key={c.label} to={c.to} data-testid={`stat-${c.label}`} className="group border border-tertiary bg-white p-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(0,0,0,0.05)]">
            <c.icon className="mb-4 h-5 w-5 text-champagne" strokeWidth={1.5} />
            <p className="font-serif text-4xl text-anthracite">{c.value ?? "—"}</p>
            <p className="mt-1 text-sm font-medium text-anthracite/80">{c.label}</p>
            <p className="text-xs text-anthracite/40">{c.sub}</p>
          </Link>
        ))}
      </div>

      <div className="mt-8 border border-tertiary bg-white p-6">
        <h2 className="mb-5 font-serif text-xl text-anthracite">Fiches par domaine</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {stats?.by_category &&
            Object.entries(stats.by_category).map(([cat, count]) => (
              <Link key={cat} to={`/admin/listings?category=${cat}`} className="flex items-center justify-between border border-tertiary px-4 py-3 text-sm transition-colors hover:border-champagne">
                <span className="text-anthracite/70">{CATEGORY_META[cat]?.fr || cat}</span>
                <span className="flex items-center gap-2 font-medium text-anthracite">{count} <ArrowRight className="h-3.5 w-3.5 text-champagne" /></span>
              </Link>
            ))}
        </div>
      </div>
    </div>
  );
}
